/**
 * Generates scoped CSS from FormStyleConfig JSON.
 *
 * NOTE: StyleConfig is intentionally defined locally (not imported from
 * @segmentflow/api-schemas) because this package is a zero-dependency
 * browser SDK bundled for CDN delivery. Default values here must match
 * the shared defaults in @segmentflow/api-schemas/src/forms/form-style.schema.ts.
 */

export interface StyleConfig {
  version: string;
  theme?: string;
  borderRadius?: number;
  fontFamily?: string;
  fontSize?: number;
  colors?: {
    background?: string;
    text?: string;
    inputBackground?: string;
    inputBorder?: string;
    inputText?: string;
    buttonBackground?: string;
    buttonText?: string;
    buttonHoverBackground?: string;
    errorText?: string;
    successBackground?: string;
    successText?: string;
  };
  spacing?: {
    padding?: number;
    gap?: number;
  };
  shadow?: string;
  width?: string;
  maxWidth?: string;
}

function getShadowValue(shadow?: string): string {
  switch (shadow) {
    case "sm":
      return "0 1px 2px rgba(0,0,0,0.05)";
    case "md":
      return "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)";
    case "lg":
      return "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)";
    default:
      return "none";
  }
}

export function generateCSS(style: StyleConfig, customCss?: string): string {
  const c = style.colors ?? {};
  const s = style.spacing ?? {};
  const radius = style.borderRadius ?? 8;
  const padding = s.padding ?? 24;
  const gap = s.gap ?? 16;
  const font = style.fontFamily
    ? `font-family: ${style.fontFamily};`
    : "";
  const fontSize = style.fontSize ? `font-size: ${style.fontSize}px;` : "";

  const css = `
:host {
  display: block;
  ${style.width ? `width: ${style.width};` : ""}
  ${style.maxWidth ? `max-width: ${style.maxWidth};` : ""}
}

.sf-form-container {
  background: ${c.background ?? "#ffffff"};
  color: ${c.text ?? "#111827"};
  padding: ${padding}px;
  border-radius: ${radius}px;
  box-shadow: ${getShadowValue(style.shadow)};
  ${font}
  ${fontSize}
  box-sizing: border-box;
}

.sf-image {
  text-align: center;
  margin-bottom: ${gap}px;
}

.sf-image img {
  max-width: 100%;
  height: auto;
}

.sf-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${gap}px;
}

.sf-heading {
  font-size: 1.25em;
  font-weight: 600;
  color: ${c.text ?? "#111827"};
  margin: 0;
}

.sf-description {
  font-size: 0.875em;
  color: ${c.text ?? "#111827"};
  opacity: 0.7;
  margin: 0;
}

.sf-lead-magnet-text {
  font-size: 0.875em;
  color: ${c.text ?? "#111827"};
  opacity: 0.8;
  margin: 0 0 ${gap}px 0;
}

.sf-form {
  display: flex;
  flex-direction: column;
  gap: ${gap}px;
}

.sf-form.sf-layout-horizontal,
.sf-form.sf-layout-inline {
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
}

.sf-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.sf-label {
  font-size: 0.875em;
  font-weight: 500;
  color: ${c.text ?? "#111827"};
}

.sf-input {
  background: ${c.inputBackground ?? "#ffffff"};
  border: 1px solid ${c.inputBorder ?? "#d1d5db"};
  color: ${c.inputText ?? "#111827"};
  border-radius: ${Math.max(4, radius - 2)}px;
  padding: 8px 12px;
  font-size: 1em;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  width: 100%;
}

.sf-input:focus {
  border-color: ${c.buttonBackground ?? "#111827"};
  box-shadow: 0 0 0 1px ${c.buttonBackground ?? "#111827"};
}

.sf-input.sf-input-error {
  border-color: ${c.errorText ?? "#ef4444"};
}

.sf-error-text {
  font-size: 0.75em;
  color: ${c.errorText ?? "#ef4444"};
  margin-top: 2px;
}

.sf-submit-btn {
  background: ${c.buttonBackground ?? "#111827"};
  color: ${c.buttonText ?? "#ffffff"};
  border: none;
  border-radius: ${Math.max(4, radius - 2)}px;
  padding: 10px 20px;
  font-size: 1em;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
}

.sf-submit-btn:hover {
  background: ${c.buttonHoverBackground ?? "#374151"};
}

.sf-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sf-success {
  text-align: center;
  padding: ${padding}px;
  background: ${c.successBackground ?? c.background ?? "#ffffff"};
  color: ${c.successText ?? c.text ?? "#111827"};
  border-radius: ${radius}px;
}

.sf-success-message {
  font-size: 1.1em;
  font-weight: 500;
}

.sf-error-banner {
  background: #fef2f2;
  color: ${c.errorText ?? "#ef4444"};
  padding: 8px 12px;
  border-radius: ${Math.max(4, radius - 2)}px;
  font-size: 0.875em;
  text-align: center;
}

${customCss ?? ""}
`.trim();

  return css;
}
